/**
 * One asset's settlement window, as a TradingView indicator.
 *
 * A research page says what an asset started and ended at; this lets a reader
 * check that against a chart they trust. It shades the window, reads the start,
 * high, low and end prices off the chart, and sets them beside the prices the
 * forecast was settled at.
 *
 * Framework code: it knows windows, not modes. Each research mode works out its
 * own window -- close to close, open to close, or the minute it was asked to
 * the same minute a session later -- and hands it over. Nothing here imports a
 * mode, and no mode's rule is written here.
 *
 * The chart reads the window the way settlement does:
 *   - regular session only, whatever the chart's extended-hours setting;
 *   - a bar belongs to the window when it closes after the start and no later
 *     than the end, so the candle covering the moment of asking counts and a
 *     candle still open at the end does not;
 *   - the start price is that covering candle's open where there is one, and
 *     otherwise the last regular-session close before the window.
 */

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Anything going inside a Pine string literal.
const pineStr = (s) => String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
const lit = (v) => `"${pineStr(v)}"`;
const DASH = '—';

/**
 * A Pine timestamp() for one end of the window: an instant (anything Date can
 * read), or a New York wall-clock time on a session date. The second form is
 * for session boundaries -- "the close on 2026-09-09" -- which Pine resolves in
 * the exchange's own zone, daylight saving included.
 */
export const pineTime = (at) => {
  // new Date(null) is 1970, not a missing value.
  if (at === null || at === undefined || at === '') return null;
  if (typeof at === 'object' && !(at instanceof Date) && at.date) {
    const [y, m, d] = String(at.date).slice(0, 10).split('-').map(Number);
    if (![y, m, d].every(Number.isFinite)) return null;
    return `timestamp("America/New_York", ${y}, ${m}, ${d}, ${at.hour ?? 0}, ${at.minute ?? 0})`;
  }
  const t = new Date(at);
  if (Number.isNaN(t.getTime())) return null;
  return `timestamp("UTC", ${t.getUTCFullYear()}, ${t.getUTCMonth() + 1}, ${t.getUTCDate()}, `
    + `${t.getUTCHours()}, ${t.getUTCMinutes()}, ${t.getUTCSeconds()})`;
};

/**
 * Which chart to open. A window that starts mid-session is priced off minute
 * candles, and on a daily chart the candle covering the moment of asking is the
 * whole day -- its open is the morning's, not the price when asked.
 */
export const chartAdvice = (window) => (window?.intraday
  ? 'Use a 1-minute chart: the window starts mid-session, and a longer bar would read the start price from the wrong moment. TradingView keeps a limited amount of 1-minute history.'
  : 'A daily chart is enough: the window runs between session opens and closes, so any timeframe reads the same prices.');

/**
 * @param {object}   o
 * @param {string}   o.symbol
 * @param {object}   o.window    { start, end, intraday?, priorClose? } -- see pineTime
 * @param {object}  [o.recorded] { start, end } the prices it was settled at
 * @param {string}  [o.title]    what the forecast was, for the table heading
 * @param {string}  [o.url]      the page it came from
 * @returns {string|null} null when the window cannot be placed on a chart
 */
export function buildWindowScript({ symbol, window, recorded = {}, title = '', url = '' }) {
  const start = pineTime(window?.start);
  const end = pineTime(window?.end);
  if (!symbol || !start || !end) return null;
  const rStart = num(recorded.start);
  const rEnd = num(recorded.end);
  const rReturn = rStart && rEnd !== null ? `${(((rEnd - rStart) / rStart) * 100).toFixed(2)}%` : null;
  const name = String(symbol);

  const rows = [
    ['"Start"', 'fmt(windowOpen)', rStart === null ? DASH : String(rStart)],
    ['"High"', 'fmt(windowHigh)', DASH],
    ['"Low"', 'fmt(windowLow)', DASH],
    ['"End" + pending', 'fmt(windowClose)', rEnd === null ? DASH : String(rEnd)],
    ['"Return"', 'pct(windowClose, windowOpen)', rReturn ?? DASH],
    // An open-to-close window is measured against the previous close too:
    // that gap is part of what the forecast was about.
    ...(window.priorClose ? [['"Previous close"', 'fmt(closeBefore)', DASH]] : []),
  ];
  const cells = rows.map(([label, chart, settled], i) => {
    const n = i + 3;
    return `    table.cell(fnTable, 0, ${n}, ${label}, text_color = color.silver, text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, ${n}, ${chart}, text_color = color.white, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 2, ${n}, ${lit(settled)}, text_color = color.yellow, text_size = size.normal, text_halign = text.align_right)`;
  }).join('\n');

  const levels = [[rStart, 'Settled start', 'color.orange'], [rEnd, 'Settled end', 'color.purple']]
    .filter(([v]) => v !== null)
    .map(([v, label, color], i) => `var line lv${i} = na
if barstate.islast and na(lv${i})
    lv${i} := line.new(x1 = startTime, y1 = ${v}, x2 = endTime, y2 = ${v}, xloc = xloc.bar_time,
         color = color.new(${color}, 0), width = 1, style = line.style_dashed)
    label.new(x = endTime, y = ${v}, xloc = xloc.bar_time, text = ${lit(`${label} ${v}`)},
         style = label.style_label_left, color = color.new(${color}, 20), textcolor = color.white, size = size.small)`)
    .join('\n');

  return `//@version=6
// ForecastNest — ${name}${title ? `, ${title}` : ''}
${url ? `// Generated from ${url}\n` : ''}//
// Open the ${name} chart on TradingView, paste this into the Pine editor and
// press Add to chart. ${chartAdvice(window)}
//
// The shaded band is the settlement window. The table reads the start, high,
// low and end prices off the chart -- regular session only, whatever the
// chart's extended-hours setting -- beside the prices this forecast was
// settled at. Prices are split-adjusted, not dividend-adjusted: leave
// "Adjust data for dividends" off. Another feed can differ in the last cent.
indicator(${lit(`ForecastNest — ${name} settlement window`)}, overlay = true)

startTime = ${start}
endTime   = ${end}

// Room on the right, so a window still running is not squeezed against the
// price scale. Only a plot with an offset makes TradingView add future bars.
padBars = input.int(20, "Empty bars after the window", minval = 0, maxval = 500)
plot(close, title = "Right margin", offset = padBars, color = color.new(color.blue, 100))

// ========== The window, read as settlement reads it ==========
inWindow = time_close > startTime and time_close <= endTime and session.ismarket

var float priorClose  = na
var float closeBefore = na
var float windowOpen  = na
var float windowHigh  = na
var float windowLow   = na
var float windowClose = na

if inWindow and na(windowOpen)
    closeBefore := priorClose
    // The candle covering the moment the window opened, or else the last
    // close before it: a window opening at the close or overnight starts from
    // that close, not from the next session's opening gap.
    windowOpen := time <= startTime or na(priorClose) ? open : priorClose

if inWindow
    windowHigh  := na(windowHigh) ? high : math.max(windowHigh, high)
    windowLow   := na(windowLow) ? low : math.min(windowLow, low)
    windowClose := close

if session.ismarket
    priorClose := close

bgcolor(inWindow ? color.new(color.blue, 90) : na, title = "Settlement window")
${levels}

fmt(v) =>
    na(v) ? "${DASH}" : str.tostring(v, "#.####")

pct(v, b) =>
    na(v) or na(b) or b == 0 ? "${DASH}" : str.tostring((v - b) / b * 100, "#.##") + "%"

pending = timenow < endTime ? "  (pending)" : ""

// ========== The table ==========
var table fnTable = table.new(position.top_right, 3, ${rows.length + 3}, bgcolor = color.new(color.black, 85), border_color = color.gray)

if barstate.islast
    table.cell(fnTable, 0, 0, ${lit(`${name}${title ? `  ·  ${title}` : ''}`)}, text_color = color.white, text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, 0, "", text_size = size.normal)
    table.cell(fnTable, 2, 0, "", text_size = size.normal)
    table.merge_cells(fnTable, 0, 0, 2, 0)
    table.cell(fnTable, 0, 1, str.format_time(startTime, "yyyy-MM-dd HH:mm", "America/New_York") + " to " + str.format_time(endTime, "yyyy-MM-dd HH:mm", "America/New_York") + " ET", text_color = color.silver, text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, 1, "", text_size = size.normal)
    table.cell(fnTable, 2, 1, "", text_size = size.normal)
    table.merge_cells(fnTable, 0, 1, 2, 1)
    table.cell(fnTable, 0, 2, "", text_size = size.normal)
    table.cell(fnTable, 1, 2, "Chart", text_color = color.gray, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 2, 2, "Settled", text_color = color.gray, text_size = size.normal, text_halign = text.align_right)
${cells}
`;
}
